import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Brain, HeartPulse, Sparkles, TrendingUp, Info, Mail, MailX, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/**
 * HealthIndicesDashboard — MHI, NHI, Cardio Health Index cards + trend chart.
 * All indices are on a 0-100 scale (higher = healthier).
 */

const ICON = { mhi: Sparkles, nhi: Brain, chi: HeartPulse };

const BAND_STYLE = {
  excellent:  { bg: "#e6f8ec", ink: "#217a3d", label: "Excellent" },
  good:       { bg: "#e9f2fb", ink: "#1360a8", label: "Good" },
  fair:       { bg: "#fff5e5", ink: "#b46a13", label: "Fair" },
  attention:  { bg: "#fdecec", ink: "#a1272b", label: "Needs attention" },
  unassessed: { bg: "#f2f3f5", ink: "#5f6672", label: "Not yet assessed" },
};

const RING = {
  excellent: "#2fa25a", good: "#3182bd", fair: "#e0a52b",
  attention: "#c94044", unassessed: "#94a3b8",
};

function ScoreRing({ score, band, size = 96 }) {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const off = c * (1 - pct);
  const color = RING[band] || RING.unassessed;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#eef1f6" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset 700ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-2xl leading-none" style={{ color }}>{score}</div>
        <div className="text-[9px] uppercase tracking-widest text-[color:var(--mano-ink-soft)] mt-0.5">/100</div>
      </div>
    </div>
  );
}

function IndexCard({ idx }) {
  const Icon = ICON[idx.id] || Sparkles;
  const style = BAND_STYLE[idx.band] || BAND_STYLE.unassessed;
  const trend = idx.trend || [];
  const first = trend[0]?.score;
  const last = trend[trend.length - 1]?.score;
  const delta = first != null && last != null ? last - first : null;

  // 15-day delta (server-computed). Prefer this over window delta for
  // the prominent trend arrow so patients see recent movement.
  const delta15 = idx.delta_15d;
  const prior15 = idx.score_15d_ago;
  const has15 = delta15 !== null && delta15 !== undefined;
  const dir = has15 ? (delta15 > 0 ? "up" : delta15 < 0 ? "down" : "flat") : null;
  const trendMeta = {
    up:   { Icon: ArrowUpRight,   color: "#217a3d", bg: "#e6f8ec", label: "Trending up" },
    down: { Icon: ArrowDownRight, color: "#a1272b", bg: "#fdecec", label: "Trending down" },
    flat: { Icon: Minus,          color: "#5f6672", bg: "#f2f3f5", label: "Holding steady" },
  }[dir || "flat"];

  return (
    <div
      data-testid={`health-card-${idx.id}`}
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-5 flex flex-col gap-4"
    >
      <div className="flex items-start gap-4">
        <ScoreRing score={idx.score} band={idx.band} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={16} className="text-[color:var(--mano-primary)]" />
            <span className="uppercase-label">{idx.code}</span>
            {idx.placeholder && (
              <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Preview
              </span>
            )}
          </div>
          <div className="font-display text-lg mt-0.5 leading-tight">{idx.name}</div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: style.bg, color: style.ink }}
            >
              {style.label}
            </span>
            {has15 && (
              <span
                data-testid={`health-card-${idx.id}-delta15`}
                className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: trendMeta.bg, color: trendMeta.color }}
                title={
                  prior15 != null
                    ? `15 days ago: ${prior15}/100 · today ${idx.score}/100`
                    : trendMeta.label
                }
              >
                <trendMeta.Icon size={12} />
                {delta15 > 0 ? "+" : ""}{delta15} pts · 15d
              </span>
            )}
            {!has15 && (
              <span
                data-testid={`health-card-${idx.id}-delta15-empty`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"
                title="Not enough history yet for a 15-day comparison"
              >
                <Info size={10} /> New baseline
              </span>
            )}
          </div>
          {delta != null && (
            <div
              className={`mt-2 inline-flex items-center gap-1 text-[10px] font-semibold ${
                delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-slate-500"
              }`}
              title={`Change over selected window`}
            >
              <TrendingUp size={10} className={delta < 0 ? "rotate-180" : ""} />
              {delta > 0 ? "+" : ""}{delta} pts over window
            </div>
          )}
        </div>
      </div>
      <p className="text-[12px] text-[color:var(--mano-ink-soft)] leading-relaxed">
        {idx.description}
      </p>
      <div className="text-[10px] text-[color:var(--mano-ink-soft)]">
        Basis: <b>{idx.sample_count}</b> {idx.sample_count === 1 ? "sample" : "samples"} · Sources: {idx.sources.join(" · ")}
      </div>
    </div>
  );
}

function TrendChart({ indices, window }) {
  // Merge each index's trend into a shared x-axis of dates.
  const merged = useMemo(() => {
    const map = new Map();
    for (const idx of indices) {
      for (const p of idx.trend || []) {
        const cur = map.get(p.date) || { date: p.date };
        cur[idx.id.toUpperCase()] = p.score;
        map.set(p.date, cur);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [indices]);

  if (merged.length < 2) {
    return (
      <div
        data-testid="health-trend-empty"
        className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-5 text-center text-sm text-[color:var(--mano-ink-soft)]"
      >
        <Info size={14} className="inline mr-1" />
        Log more voice screenings to see your trend over {window} days.
      </div>
    );
  }

  const colors = { MHI: "#5b7fb2", NHI: "#8b5cf6", CHI: "#d76b8f" };
  return (
    <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-4 sm:p-5" data-testid="health-trend-chart">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="uppercase-label inline-flex items-center gap-1">
          <TrendingUp size={13} /> {window}-day trend
        </div>
        <div className="flex items-center gap-3 text-[11px] font-semibold">
          {Object.entries(colors).map(([k, c]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full" style={{ background: c }} />
              {k}
            </span>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={merged} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748b" }} width={38} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #e5e7eb" }}
              formatter={(v) => `${v}/100`}
            />
            {["MHI", "NHI", "CHI"].map((k) => (
              <Line
                key={k} type="monotone" dataKey={k} stroke={colors[k]}
                strokeWidth={2.4} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function HealthIndicesDashboard() {
  const [data, setData] = useState(null);
  const [window, setWindow] = useState(90);
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    api.get(`/me/health-indices?window=${window}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch(() => setErr("Could not load health indices."));
  }, [window]);

  useEffect(() => {
    api.get("/me/digest/preview")
      .then((r) => setOptedOut(!!r.data.opted_out))
      .catch(() => {});
  }, []);

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/me/digest/send-now");
      toast.success(r.data.sent ? "15-day digest sent to your inbox." : "Not enough data yet.");
    } catch {
      toast.error("Couldn't send the digest right now.");
    } finally {
      setSending(false);
    }
  };

  const toggleOptOut = async () => {
    try {
      const r = await api.post(optedOut ? "/me/digest/opt-in" : "/me/digest/opt-out");
      setOptedOut(!!r.data.opted_out);
      toast.success(r.data.opted_out ? "Digest paused." : "Digest resumed.");
    } catch {
      toast.error("Couldn't update your digest preference.");
    }
  };

  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!data) return null;
  return (
    <section data-testid="health-indices-dashboard" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="uppercase-label inline-flex items-center gap-1">
            <TrendingUp size={13} /> Voice-derived Health Indices
          </div>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-1">
            Higher is healthier. Fortnightly digest lands every 15 days.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="digest-send-now"
            onClick={sendDigestNow}
            disabled={sending || optedOut}
            title={optedOut ? "You've paused the digest — resume to send one." : ""}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-[color:var(--mano-border)] bg-white hover:bg-[color:var(--mano-mild)]/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail size={12} /> {sending ? "Sending…" : "Email me the digest"}
          </button>
          <button
            data-testid="digest-toggle-opt"
            onClick={toggleOptOut}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              optedOut
                ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                : "border-[color:var(--mano-border)] bg-white text-[color:var(--mano-ink-soft)] hover:bg-[color:var(--mano-mild)]/50"
            }`}
          >
            {optedOut ? <><Mail size={12} /> Resume digest</> : <><MailX size={12} /> Pause digest</>}
          </button>
          <div
            className="inline-flex rounded-full border border-[color:var(--mano-border)] bg-white p-1 text-xs"
            data-testid="health-window-toggle"
          >
            {[30, 90].map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                data-testid={`health-window-${w}`}
                className={`px-3 py-1 rounded-full font-semibold transition-colors ${
                  window === w ? "bg-[color:var(--mano-primary)] text-white" : "text-[color:var(--mano-ink-soft)]"
                }`}
              >
                {w} days
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.indices.map((i) => <IndexCard key={i.id} idx={i} />)}
      </div>
      <TrendChart indices={data.indices} window={data.window_days} />
    </section>
  );
}
